/**
 * Web Share API Helper
 * Share content FROM your app TO other apps (Walmart, Amazon, etc.)
 */

/**
 * Check if Web Share API is available
 */
export function isShareAvailable() {
  return !!(navigator.share && navigator.canShare);
}

/**
 * Share shopping list to other apps
 */
export async function shareShoppingList(items, familyName = 'Family') {
  try {
    if (!isShareAvailable()) {
      return {
        success: false,
        error: 'Sharing not supported on this device',
        fallback: true
      };
    }

    // Format shopping list as text
    const listText = items.map((item, index) => 
      `${index + 1}. ${item.name}${item.quantity ? ` (${item.quantity})` : ''}`
    ).join('\n');

    const shareData = {
      title: `${familyName} Shopping List`,
      text: `Shopping List:\n\n${listText}\n\n📱 Created with Family Hub`,
      // url: window.location.origin // Optional: link back to your app
    };

    // Check if we can share this data
    if (navigator.canShare && !navigator.canShare(shareData)) {
      throw new Error('Cannot share this type of content');
    }

    await navigator.share(shareData);
    
    console.log('✅ Shopping list shared successfully');
    return { success: true };
    
  } catch (error) {
    // User cancelled share - not an error
    if (error.name === 'AbortError') {
      console.log('Share cancelled by user');
      return { success: false, cancelled: true };
    }
    
    console.error('Error sharing:', error);
    return {
      success: false,
      error: error.message,
      fallback: true
    };
  }
}

/**
 * Share recipe to other apps
 */
export async function shareRecipe(recipe) {
  try {
    if (!isShareAvailable()) {
      return {
        success: false,
        error: 'Sharing not supported on this device',
        fallback: true
      };
    }

    // Format recipe
    let recipeText = `${recipe.title}\n\n`;
    
    if (recipe.description) {
      recipeText += `${recipe.description}\n\n`;
    }
    
    if (recipe.ingredients && recipe.ingredients.length > 0) {
      recipeText += 'Ingredients:\n';
      recipe.ingredients.forEach(ing => {
        recipeText += `• ${ing.amount || ''} ${ing.unit || ''} ${ing.name}\n`;
      });
      recipeText += '\n';
    }
    
    if (recipe.instructions && recipe.instructions.length > 0) {
      recipeText += 'Instructions:\n';
      recipe.instructions.forEach((step, index) => {
        recipeText += `${index + 1}. ${step}\n`;
      });
    }

    const shareData = {
      title: recipe.title,
      text: recipeText
    };

    // Try to share with image if available
    if (recipe.photoURL) {
      try {
        const response = await fetch(recipe.photoURL);
        const blob = await response.blob();
        const file = new File([blob], 'recipe.jpg', { type: blob.type });
        shareData.files = [file];
      } catch (err) {
        console.log('Could not include image in share:', err);
      }
    }

    if (navigator.canShare && !navigator.canShare(shareData)) {
      // Can't share with files, try without
      delete shareData.files;
    }

    await navigator.share(shareData);
    
    console.log('✅ Recipe shared successfully');
    return { success: true };
    
  } catch (error) {
    if (error.name === 'AbortError') {
      return { success: false, cancelled: true };
    }
    
    console.error('Error sharing recipe:', error);
    return {
      success: false,
      error: error.message,
      fallback: true
    };
  }
}

/**
 * Share task to other apps
 */
export async function shareTask(task, assignedMembers = []) {
  try {
    if (!isShareAvailable()) {
      return {
        success: false,
        error: 'Sharing not supported on this device',
        fallback: true
      };
    }

    const memberNames = assignedMembers.map(m => m.displayName).join(', ');
    
    let taskText = `📋 ${task.title}\n\n`;
    
    if (task.description) {
      taskText += `${task.description}\n\n`;
    }
    
    if (memberNames) {
      taskText += `Assigned to: ${memberNames}\n`;
    }
    
    if (task.dueDate) {
      taskText += `Due: ${new Date(task.dueDate).toLocaleDateString()}\n`;
    }
    
    if (task.allowance) {
      taskText += `Reward: $${task.allowance.toFixed(2)}\n`;
    }

    const shareData = {
      title: task.title,
      text: taskText
    };

    await navigator.share(shareData);
    
    console.log('✅ Task shared successfully');
    return { success: true };
    
  } catch (error) {
    if (error.name === 'AbortError') {
      return { success: false, cancelled: true };
    }
    
    console.error('Error sharing task:', error);
    return {
      success: false,
      error: error.message,
      fallback: true
    };
  }
}

/**
 * Share to specific apps (Walmart, Amazon, etc.)
 */
export async function shareToWalmart(items) {
  // Walmart grocery app can accept shared shopping lists
  // The app will handle the text import
  return await shareShoppingList(items, 'Family');
}

export async function shareToAmazon(items) {
  // Amazon app can accept shared shopping lists
  // The app will handle the text import
  return await shareShoppingList(items, 'Family');
}

/**
 * Copy to clipboard as fallback
 */
export async function copyToClipboard(text) {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return { success: true };
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      const success = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      return { success };
    }
  } catch (error) {
    console.error('Error copying to clipboard:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get formatted shopping list text for sharing
 */
export function formatShoppingListForShare(items, includeCategories = false) {
  if (includeCategories) {
    // Group by category
    const grouped = items.reduce((acc, item) => {
      const category = item.category || 'Other';
      if (!acc[category]) acc[category] = [];
      acc[category].push(item);
      return acc;
    }, {});

    let text = '';
    Object.entries(grouped).forEach(([category, categoryItems]) => {
      text += `\n${category}:\n`;
      categoryItems.forEach(item => {
        text += `  • ${item.name}${item.quantity ? ` (${item.quantity})` : ''}`;
        if (item.store) text += ` - ${item.store}`;
        text += '\n';
      });
    });
    
    return text;
  } else {
    // Simple list
    return items.map(item => 
      `• ${item.name}${item.quantity ? ` (${item.quantity})` : ''}`
    ).join('\n');
  }
}












